import React from "react";
import BaseModel from "../models/BaseModel";
import type Group from "../models/Group";
import Page from "../models/Page";
import TreeViewModel, { DragMode, DropResult } from "../models/TreeviewModel";
import Logger from "../Logger";
import TreeMenuIcon from "../components/NavigationTree2/TreeMenuIcon";

import type Consumer from "../models/Consumer";
import type DeviceTemplate from "../models/DeviceTemplate";
import type Fuse from "../models/Fuse";
import Point from "../models/Point";
import { noItx } from "../translationItx/itx";
import { PageTypes } from "../models/PageTypes";
import { C1_Furniture } from "../../ConceptOne/src/model/C1_Furniture";
import { C1_Opening } from "../../ConceptOne/src/model/C1_Opening";
import { C1_EloGroup } from "../../ConceptOne/src/model/C1_EloGroup";
import { getC1EloDefinitionIcon } from "../../ConceptOne/src/furniture/C1_EloDefinitionIcons";
import {
  buildC1DefinitionAddMenu,
  getC1ContainerDefinition,
} from "../../ConceptOne/src/model/C1_NavigatorDefinitionMenu";

const logger = new Logger();

export default class GroupTreeViewModel extends TreeViewModel {
  get typedObj(): Group {
    return this.obj as Group;
  }

  get isFolder(): boolean {
    return true;
  }

  get childCollection(): Array<BaseModel> | undefined {
    if (state.flattenGroups.value) {
      let res: Array<BaseModel> = this.typedObj.allConsumers;
      res = res.concat(this.typedObj.allButtons);
      return res;
    } else {
      let res: Array<BaseModel> = this.typedObj.groups.sortBy("tagOrName");
      res = res.concat(this.typedObj.consumers.sortBy("tagOrName"));
      res = res.concat(this.typedObj.buttons.sortBy("tagOrName"));
      return res;
    }
  }

  get iconUrl(): string | undefined {
    if (this.typedObj) {
      return "lab la-codepen";
    }

    return undefined;
  }

  override getIcon(): React.ReactNode | undefined {
    const c1 = this.typedObj.c1;
    let definitionIcon: React.ReactNode | undefined;

    if (c1 instanceof C1_EloGroup) {
      definitionIcon = getC1EloDefinitionIcon(c1.definition);
    } else if (c1 instanceof C1_Furniture || c1 instanceof C1_Opening) {
      definitionIcon = c1.definition?.icon;
    }

    if (definitionIcon) {
      const definitionIconClass = c1 instanceof C1_Opening ? " C1_groupDefinitionIcon--opening"
        : c1 instanceof C1_Furniture && c1.definitionId === "wallbox" ? " C1_groupDefinitionIcon--wallbox" : "";
      return (
        <div
          className={`icon navIcon C1_groupDefinitionIcon${definitionIconClass}`}
          style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}
        >
          {definitionIcon}
        </div>
      );
    }
    return super.getIcon();
  }

  getFullTitle(): string | undefined | JSX.Element {
    if (this.typedObj.c1 instanceof C1_EloGroup) {
      return this.typedObj.displayName;
    }
    // Use the regular tag/name rendering, which resolves Group.getName().
    return super.getFullTitle();
  }

  override getNameNeeded(): boolean {
    return !(this.typedObj.c1 instanceof C1_EloGroup);
  }

  override getTagNeeded(): boolean {
    return !(this.typedObj.c1 instanceof C1_EloGroup);
  }

  // getPropertyControl(setPinned: (pinned: boolean) => void): any {
  //   if (this.typedObj.tag.startsWith("@I")) {
  //     return undefined;
  //   }

  //   if (this.isFloorGeneral()) {
  //     return super.getPropertyControl(setPinned);
  //   }
  //   return <GroupPropertyView obj={this.obj} setPinned={setPinned} />;
  // }

  // handleOpen(transitOpen: boolean = false): boolean {
  // }

  ////////////////////////////////////////////////////////////////////////////////////////

  // buildAddConsumerMenuItems(point?: Point): any {
  //   const items: any[] = [];
  //   let i = 0;
  //   const tbi = consumerItems.concat(buttonItems, permanentPowerItems, sensorItems, distributionItems);
  //   tbi.forEach((item) => {
  //     const imageStyle: any = { width: "auto", height: "25px", objectFit: "contain" };
  //     const img = <img draggable="false" style={imageStyle} src={item.image} />; // no itx
  //     items.push({
  //       id: `item${i++}`,
  //       image: img,
  //       label: (item as any).titleShort,
  //       onClick: () => {
  //         const page = this.typedObj.getOrCreatePage();
  //         if (page) {
  //           const pPX = point || page.findFreePositionForNewObject();
  //           if (
  //             (item as any).classification?.name == "Button" ||
  //             (item as any).classification?.name == "SensorNew" ||
  //             (item as any).classification?.name == "DecentralActor"
  //           ) {
  //             const button = page.createNewButtonInPage(this.typedObj, item, pPX);
  //             if ((item as any).classification?.name == "SensorNew" && button) {
  //               button.classification = DeviceClasses.SensorNew;
  //             } else if ((item as any).classification?.name == "DecentralActor" && button) {
  //               button.classification = DeviceClasses.DecentralActor;
  //             }
  //             if (button) {
  //               state.eventLogger.addEvent(EventKey.ObjectCreated, {
  //                 objectType: "Button",
  //                 obj: button.id + "_" + button.tag + "_" + button.name,
  //               });
  //             }
  //             state.selectionManager.selectedObject = button;
  //           } else {
  //             page.createNewConsumerInPage(this.typedObj, item, pPX).then((consumer) => {
  //               if (consumer) {
  //                 state.eventLogger.addEvent(EventKey.ObjectCreated, {
  //                   objectType: "Consumer" + (consumer.isDali ? "Dali" : "") + consumer.channelType,
  //                   obj: consumer.id + "_" + consumer.tag + "_" + consumer.name,
  //                 });
  //               }
  //               state.selectionManager.selectedObject = consumer;
  //             });
  //           }
  //         }
  //       },
  //     });
  //   });
  //   return { title: state.translate("GroupTreeViewModel.add", "Hinzufügen"), items };
  // }

  handleAddWithEvent(event: any) {
    this.handleAddConsumer(event);
  }

  handleAddConsumer(event: any, point?: Point) {
    const definition = getC1ContainerDefinition(this.typedObj);
    if (!definition) return;
    const menuItems = buildC1DefinitionAddMenu(this.typedObj, definition);
    state.contextMenuManager.show1(event, menuItems, menuItems.disabledItemIds);
  }

  // getButtonsAsContextMenuItems(): any[] {
  //   // Return all auto-derived button items, but replace the "plus-circle" button with
  //   // a native Submenu so clicking it shows a side panel instead of a replacement menu.
  //   return this.buttonsAsContextMenuItems().map((item) => {
  //     if (item.id === "btn_plus-circle") {
  //       return {
  //         ...item,
  //         menu: this.buildAddConsumerMenuItems(),
  //         onClick: undefined,
  //       };
  //     }
  //     return item;
  //   });
  // }

  inlineButton_Add() {
    return (
      <TreeMenuIcon
        onClick={(ev: any) => {
          this.handleAddConsumer(ev);
        }}
        iconName="plus-circle"
        tooltipKey="inlineButton.addFunctionButtonConsumer" // no itx
        objName={this.obj.className + this.obj.fullTag}
        key={this.obj.className + this.obj.fullTag + "autoConnect"} // no itx
      />
    );
  }

  ////////////////////////////////////////////////////////////////////////////////////////
  //
  // inline menu buttons
  //super.inlineButton_Properties(),
  ////////////////////////////////////////////////////////////////////////////////////////

  getButtons(): JSX.Element[] | undefined {
    if (getC1ContainerDefinition(this.typedObj)) return [this.inlineButton_Add()];
    return [];
  }

  ////////////////////////////////////////////////////////////////////////////////////////
  //
  // context menu
  //
  ////////////////////////////////////////////////////////////////////////////////////////

  handleProperties() {
    // state.dialogManager.openDialog(<GroupDialog parent={this.obj.parent as Floor} obj={this.typedObj} />);
  }

  ////////////////////////////////////////////////////////////////////////////////////////

  canBePastedInteractive(copiedObjClassName: string): boolean {
    if (copiedObjClassName == "Group" || copiedObjClassName == "Consumer" || copiedObjClassName == "Button") {
      return true;
    }
    return false;
  }

  async getDisabledContextMenuItems(): Promise<Array<string>> {
    const copiedObjClassName = state.clipboard.getClassName();

    if (
      (copiedObjClassName && copiedObjClassName == "Group") ||
      (copiedObjClassName && copiedObjClassName == "Floor") ||
      (copiedObjClassName && copiedObjClassName == "Consumer") ||
      (copiedObjClassName && copiedObjClassName == "Button") ||
      (copiedObjClassName && copiedObjClassName == "Actor")
    ) {
      return new Array<string>();
    }

    return ["paste"]; // no itx
  }

  get contextMenuItems(): any {
    let subLabelPaste = undefined;

    try {
      subLabelPaste = state.contextMenuManager.clipboardData?.items?.[0]?.name;
    } catch (ex) {
      logger.error(ex);
    }

    return {
      title: this.obj?.name,
      items: [
        this.baseContextMenuItems_Copy(),
        // this.baseContextMenuItems_Cut(),
        this.baseContextMenuItems_Paste(subLabelPaste),
        this.baseContextMenuItems_Delete(),
        // this.baseContextMenuItems_Properties(),
      ],
    };
  }

  allowDrag(): boolean {
    return true;
  }

  allowDrop(event: any): boolean {
    return true;
  }

  async handleDrop2(mode: DragMode, obj: BaseModel): Promise<DropResult> {
    try {
      const Group = this.typedObj;
      const page = Group.parentRoom?.getOrCreatePage();
      const pPX = page?.findFreePositionForNewObject();

      if (mode == DragMode.copy && obj.className != "DeviceTemplate") {
        // copy object
        await state.menubarActions.onCopy(obj);
        await state.menubarActions.onPaste(this.typedObj);
        return new DropResult(true);
      } else {
        // move or copy button to this Group

        switch (obj.className) {
          case noItx("Button"):
          case noItx("Consumer"):
            //   let errorMessage = itx(
            //   "floorTreeViewModel.relocationFromT1ToT2NotAllowed",
            //   "Das verschieben von T1 in die Etage T2 ist nicht erlaubt!",
            //   { T1: obj.tag + " " + obj.name, T2: this.typedObj.tag + " " + this.typedObj.name },
            // );
            // return new DropResult(false, errorMessage);
            const consumer = obj as Consumer;
            const moved = consumer.moveToNewParent(this.typedObj);
            if (moved) {
              state.selectionManager.selectedObject = consumer;
              return new DropResult(true);
            } else {
              return new DropResult(false);
            }

          case noItx("DeviceTemplate"):
            if (!page || !pPX) return new DropResult(false);
            const isActor = obj && obj.className == "DeviceTemplate" && (obj as DeviceTemplate).isActor;

            const button = page.createNewButtonInPage(Group.parentRoom!, obj, pPX);
            if (button) {
              button.moveToNewParent(Group);
              if (!isActor) {
                //// check is Actor is allowed to be used decentral !?!?!.....
              }
              state.selectionManager.selectedObject = button;
              return new DropResult(true);
            } else {
              return new DropResult(false, `Drag&Drop ist nicht erlaubt für Aktoren`);
            }

            break;

          case noItx("Page"):
            if (!page) return new DropResult(false);
            const pageDraged = obj as Page;
            if (pageDraged.documentType.type == PageTypes.pdf) {
              // set here the backgroundImgUrl
              const dpmm = 30;
              const backgrounimageBefore = page.backgroundImgUrl;
              await pageDraged.pdfFile
                .convertPdfToDataUrl({
                  pageNumber: 1,
                  targetWidth: page.pagesize.width * dpmm,
                  targetHeight: page.pagesize.height * dpmm,
                  rotation: page.pdfRotation || 0,
                })
                .then((imgUrl) => {
                  page.resizeImageAfterLoad(imgUrl).then((result) => {
                    page.backgroundImgUrl = result as string;
                  });
                });

              page.raiseOnChange();
              return new DropResult(true);
            }
            break;
          case noItx("Fuse"):
            this.typedObj.parentRoom?.conectAll230ConsumersWihFuse(obj as Fuse);
            return new DropResult(true);
        }
      }
    } catch (ex) {
      logger.error(ex);
    }
    return new DropResult(false);
  }
}
